import React, { useState, useCallback } from "react";
import { GoogleMap, Marker, Circle, Autocomplete, useLoadScript } from "@react-google-maps/api";

/**
 * Repeater of "key locations" an investor wants to be near. Each row: a label, an
 * address (Google Places autocomplete → lat/lng), a draggable map marker, and a radius
 * (miles) shown as a circle + slider.
 *
 * Controlled: `value` is an array of { label, lat, lng, radius_mi }; `onChange(next)`.
 * The matcher hard-intersects these (a listing must be within EVERY radius).
 */

// Must be a stable module-level constant — useLoadScript reloads if this array identity changes.
const LIBRARIES = ["places"];
const MI_TO_M = 1609.34;
const NYC = { lat: 40.7128, lng: -74.006 };
const DEFAULT_RADIUS_MI = 0.5;

const cellInput = {
  width: "100%", padding: "9px 11px", border: "1px solid #d5dae0", borderRadius: "8px",
  fontSize: "13px", boxSizing: "border-box",
};
const mapContainerStyle = { width: "100%", height: "220px", borderRadius: "8px" };
const circleOptions = {
  fillColor: "#1976d2", fillOpacity: 0.12, strokeColor: "#1976d2", strokeOpacity: 0.5, strokeWeight: 1,
  clickable: false, draggable: false, editable: false, zIndex: 1,
};

function LocationRow({ loc, onPatch, onRemove, canRemove }) {
  const [ac, setAc] = useState(null);
  const hasPoint = typeof loc.lat === "number" && typeof loc.lng === "number";
  const center = hasPoint ? { lat: loc.lat, lng: loc.lng } : NYC;
  const radiusMi = loc.radius_mi || DEFAULT_RADIUS_MI;

  const onPlaceChanged = useCallback(() => {
    if (!ac) return;
    const place = ac.getPlace();
    const g = place?.geometry?.location;
    if (!g) return;
    onPatch({
      lat: g.lat(), lng: g.lng(),
      label: loc.label || place.name || place.formatted_address || "",
      address: place.formatted_address || place.name || "",
    });
  }, [ac, loc.label, onPatch]);

  return (
    <div style={{ border: "1px solid #e6e9ee", borderRadius: "10px", padding: "12px 14px", backgroundColor: "#fafbfc" }}>
      <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
        <input
          style={{ ...cellInput, flex: "0 0 34%" }}
          placeholder="Label (e.g. Office)"
          value={loc.label || ""}
          onChange={(e) => onPatch({ label: e.target.value })}
        />
        <div style={{ flex: 1 }}>
          <Autocomplete onLoad={setAc} onPlaceChanged={onPlaceChanged} fields={["geometry", "name", "formatted_address"]}>
            <input style={cellInput} placeholder="Search an address…" defaultValue={loc.address || ""} />
          </Autocomplete>
        </div>
        {canRemove && (
          <button type="button" onClick={onRemove} aria-label="Remove location"
            style={{ border: "none", background: "none", color: "#a5281b", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}>
            Remove
          </button>
        )}
      </div>

      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={center}
        zoom={hasPoint ? 14 : 11}
        options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
      >
        {hasPoint && (
          <>
            <Marker
              position={center}
              draggable
              onDragEnd={(e) => onPatch({ lat: e.latLng.lat(), lng: e.latLng.lng() })}
            />
            <Circle center={center} radius={radiusMi * MI_TO_M} options={circleOptions} />
          </>
        )}
      </GoogleMap>

      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginTop: "10px" }}>
        <span style={{ fontSize: "12.5px", color: "#5f6b78", fontWeight: 600, whiteSpace: "nowrap" }}>Radius</span>
        <input
          type="range" min="0.1" max="3" step="0.05" value={radiusMi}
          onChange={(e) => onPatch({ radius_mi: Number(e.target.value) })}
          style={{ flex: 1 }}
        />
        <span style={{ fontSize: "12.5px", color: "#1a1f26", fontWeight: 600, whiteSpace: "nowrap", minWidth: "56px", textAlign: "right" }}>
          {radiusMi.toFixed(2)} mi
        </span>
      </div>
    </div>
  );
}

export default function KeyLocationsEditor({ value, onChange }) {
  const rows = Array.isArray(value) ? value : [];
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  const patch = (i, p) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...p } : r)));
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));
  const add = () => onChange([...rows, { label: "", radius_mi: DEFAULT_RADIUS_MI }]);

  if (loadError) return <div style={{ fontSize: "13px", color: "#a5281b" }}>Failed to load Google Maps.</div>;
  if (!isLoaded) return <div style={{ fontSize: "13px", color: "#8a929b" }}>Loading map…</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {rows.map((loc, i) => (
        <LocationRow
          key={i}
          loc={loc}
          onPatch={(p) => patch(i, p)}
          onRemove={() => remove(i)}
          canRemove={rows.length > 0}
        />
      ))}
      <button type="button" onClick={add}
        style={{
          alignSelf: "flex-start", border: "1px dashed #a9b4c0", background: "none", color: "#1976d2",
          cursor: "pointer", fontSize: "13px", fontWeight: 600, padding: "8px 14px", borderRadius: "8px",
        }}>
        + Add location
      </button>
    </div>
  );
}
